import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Shuffle, Eraser, Check, Trophy, Sparkles, Loader2, Award, HelpCircle, Timer, Flag, Play, Zap, ArrowLeft } from "lucide-react";

/* ------------------------------------------------------------------ *
 * Honeycomb geometry (pointy-top, odd-r offset). We compute pixel
 * centres so the SVG comb and the letter overlay share identical
 * geometry -> the "invisible grid" always matches the background.
 * ------------------------------------------------------------------ */
const ROWS = 7;
const COLS = 6; // 42 tiles
const SIZE = 34; // hex radius (centre -> vertex), px
const W = Math.sqrt(3) * SIZE; // hex width
const H = 2 * SIZE; // hex height
const ROW_STEP = 1.5 * SIZE; // vertical distance between rows
const PAD = 10;

const ODDR_DIRS = [
  [[+1, 0], [0, -1], [-1, -1], [-1, 0], [-1, +1], [0, +1]], // even rows
  [[+1, 0], [+1, -1], [0, -1], [-1, 0], [0, +1], [+1, +1]], // odd rows
];

const TILES = [];
for (let r = 0; r < ROWS; r++) {
  for (let c = 0; c < COLS; c++) {
    const cx = PAD + W / 2 + c * W + (r & 1 ? W / 2 : 0);
    const cy = PAD + H / 2 + r * ROW_STEP;
    TILES.push({ id: `${r}-${c}`, row: r, col: c, cx, cy });
  }
}
const BOARD_W = Math.max(...TILES.map((t) => t.cx)) + W / 2 + PAD;
const BOARD_H = Math.max(...TILES.map((t) => t.cy)) + H / 2 + PAD;

function hexPoints(cx, cy) {
  return [0, 1, 2, 3, 4, 5]
    .map((i) => {
      const a = (Math.PI / 180) * (60 * i - 30);
      return `${(cx + SIZE * Math.cos(a)).toFixed(2)},${(cy + SIZE * Math.sin(a)).toFixed(2)}`;
    })
    .join(" ");
}

function neighbourIds(row, col) {
  return ODDR_DIRS[row & 1]
    .map(([dc, dr]) => [row + dr, col + dc])
    .filter(([r, c]) => r >= 0 && r < ROWS && c >= 0 && c < COLS)
    .map(([r, c]) => `${r}-${c}`);
}
const NEIGHBORS = {};
TILES.forEach((t) => { NEIGHBORS[t.id] = neighbourIds(t.row, t.col); });

/* ------------------------------------------------------------------ *
 * Letters
 * ------------------------------------------------------------------ */
const FREQ =
  "aaaaaaaaabbccddddeeeeeeeeeeeeffggghhiiiiiiiiijkllllmmnnnnnnooooooooppqrrrrrrssssttttttuuuuvvwwxyyz";
const VOWELS = "aeiou";
const isVowel = (ch) => VOWELS.includes(ch);
const randInt = (n) => Math.floor(Math.random() * n);
const sampleLetter = () => FREQ[randInt(FREQ.length)];

function generateLetters(n) {
  const arr = Array.from({ length: n }, () => sampleLetter());
  const minVowels = Math.round(n * 0.38);
  let vowels = arr.filter(isVowel).length;
  let guard = 0;
  while (vowels < minVowels && guard < 500) {
    const cons = arr.map((ch, i) => (isVowel(ch) ? -1 : i)).filter((i) => i >= 0);
    if (!cons.length) break;
    arr[cons[randInt(cons.length)]] = VOWELS[randInt(VOWELS.length)];
    vowels++; guard++;
  }
  return arr;
}
function shuffleArray(a) {
  const arr = [...a];
  for (let i = arr.length - 1; i > 0; i--) { const j = randInt(i + 1); [arr[i], arr[j]] = [arr[j], arr[i]]; }
  return arr;
}
function lettersToMap(letters) {
  const m = {};
  TILES.forEach((t, i) => { m[t.id] = letters[i]; });
  return m;
}

/* Scoring: 1/letter, +10 per letter 6-9, +20 per letter 10+ */
function scoreWord(word) {
  const n = word.length;
  let s = n;
  for (let i = 6; i <= n; i++) s += i >= 10 ? 20 : 10;
  return s;
}

/* Dictionary (fetched once, cached) */
let dictPromise = null;
function loadDictionary() {
  if (!dictPromise) {
    const base = process.env.PUBLIC_URL || "";
    dictPromise = fetch(`${base}/word-game-dictionary.txt`)
      .then((r) => { if (!r.ok) throw new Error("dict"); return r.text(); })
      .then((txt) => new Set(txt.split("\n").map((w) => w.trim()).filter(Boolean)))
      .catch((e) => { dictPromise = null; throw e; });
  }
  return dictPromise;
}

/* Difficulty -> seconds */
const DIFFICULTIES = [
  { key: "easy", label: "Easy", seconds: 300, blurb: "5:00 on the clock", icon: "🐝" },
  { key: "medium", label: "Medium", seconds: 180, blurb: "3:00 — keep buzzing", icon: "🍯" },
  { key: "hard", label: "Hard", seconds: 90, blurb: "1:30 — hive alert!", icon: "⚡" },
];
const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/* ================================================================== */
export default function WordGame() {
  const { user } = useAuth();

  const [dict, setDict] = useState(null);
  const [dictError, setDictError] = useState(false);

  const [phase, setPhase] = useState("setup"); // "setup" | "playing" | "over"
  const [difficulty, setDifficulty] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);

  const [letters, setLetters] = useState(() => lettersToMap(generateLetters(TILES.length)));
  const [selection, setSelection] = useState([]);
  const [found, setFound] = useState([]);
  const [gameScore, setGameScore] = useState(0);
  const [result, setResult] = useState(null); // {score, words, newRecord, bestScore, bestWord}

  const [myBest, setMyBest] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showHelp, setShowHelp] = useState(false);

  const scoreRef = useRef(0);
  const foundRef = useRef([]);
  const phaseRef = useRef(phase);
  useEffect(() => { scoreRef.current = gameScore; }, [gameScore]);
  useEffect(() => { foundRef.current = found; }, [found]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const currentWord = useMemo(() => selection.map((id) => letters[id]).join(""), [selection, letters]);
  const foundWordsSet = useMemo(() => new Set(found.map((f) => f.word)), [found]);

  /* ---- data loading ---- */
  useEffect(() => { loadDictionary().then(setDict).catch(() => setDictError(true)); }, []);

  const loadStats = useCallback(() => {
    api.get("/word-game/me").then((r) => setMyBest(r.data)).catch(() => {});
    api.get("/word-game/leaderboard", { params: { limit: 10 } }).then((r) => setLeaderboard(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  /* ---- score submission ---- */
  const submitGame = useCallback(async (score, words) => {
    if (!score || score <= 0) return null;
    const best = words.reduce(
      (acc, w) => (w.score > acc.best_word_score ? { best_word: w.word, best_word_score: w.score } : acc),
      { best_word: "", best_word_score: 0 },
    );
    try {
      const { data } = await api.post("/word-game/score", {
        score,
        words_found: words.length,
        best_word: best.best_word.toUpperCase(),
        best_word_score: best.best_word_score,
      });
      setMyBest({
        best_score: data.best_score, games_played: data.games_played,
        words_found_total: data.words_found_total, best_word: data.best_word,
        best_word_score: data.best_word_score,
      });
      loadStats();
      return data;
    } catch { return null; }
  }, [loadStats]);

  /* ---- end / start game ---- */
  const endGame = useCallback(async () => {
    setPhase("over");
    setSelection([]);
    const score = scoreRef.current;
    const words = foundRef.current;
    const bestW = words.reduce((a, w) => (w.score > a.s ? { w: w.word, s: w.score } : a), { w: "", s: 0 });
    const res = await submitGame(score, words);
    setResult({
      score,
      words: words.length,
      newRecord: !!res?.is_new_record,
      bestScore: res?.best_score ?? score,
      bestWord: bestW.w,
    });
    if (res?.is_new_record) toast.success(`New personal best: ${res.best_score} pts! 🏆`);
  }, [submitGame]);

  const endGameRef = useRef(endGame);
  useEffect(() => { endGameRef.current = endGame; }, [endGame]);

  const startGame = useCallback((diff) => {
    setDifficulty(diff);
    setLetters(lettersToMap(generateLetters(TILES.length)));
    setSelection([]);
    setFound([]);
    setGameScore(0);
    setResult(null);
    setTimeLeft(diff.seconds);
    setPhase("playing");
  }, []);

  /* ---- countdown ---- */
  useEffect(() => {
    if (phase !== "playing") return undefined;
    const t = setInterval(() => {
      setTimeLeft((s) => {
        if (s <= 1) { clearInterval(t); endGameRef.current(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [phase]);

  /* ---- submit game on unmount if a game is in progress ---- */
  useEffect(() => () => {
    if (phaseRef.current === "playing" && scoreRef.current > 0) submitGame(scoreRef.current, foundRef.current);
  }, [submitGame]);

  /* ---- tile interaction ---- */
  const handleTileClick = useCallback((id) => {
    if (phaseRef.current !== "playing") return;
    setSelection((sel) => {
      if (sel.length === 0) return [id];
      const last = sel[sel.length - 1];
      if (id === last) return sel.slice(0, -1);
      if (sel.includes(id)) return sel;
      if (!NEIGHBORS[last].includes(id)) return sel;
      return [...sel, id];
    });
  }, []);

  const submitWord = useCallback(() => {
    if (phaseRef.current !== "playing") return;
    const word = currentWord.toLowerCase();
    if (word.length < 3) { toast.error("Words must be at least 3 letters."); return; }
    if (foundWordsSet.has(word)) { toast.error(`"${word.toUpperCase()}" is already in your list.`); setSelection([]); return; }
    if (!dict || !dict.has(word)) { toast.error(`"${word.toUpperCase()}" isn't in the dictionary.`); setSelection([]); return; }
    const pts = scoreWord(word);
    setFound((f) => [{ word, score: pts }, ...f]);
    setGameScore((s) => s + pts);
    setSelection([]);
    toast.success(`${word.toUpperCase()} +${pts} pts`, { description: word.length >= 6 ? "Bonus for a long word! 🐝" : undefined });
  }, [currentWord, dict, foundWordsSet]);

  const scramble = useCallback(() => {
    setLetters((m) => lettersToMap(shuffleArray(TILES.map((t) => m[t.id]))));
    setSelection([]);
  }, []);

  /* ---- keyboard ---- */
  useEffect(() => {
    const onKey = (e) => {
      if (phaseRef.current !== "playing") return;
      if (e.key === "Enter") { e.preventDefault(); submitWord(); }
      else if (e.key === "Backspace") { e.preventDefault(); setSelection((s) => s.slice(0, -1)); }
      else if (e.key === "Escape") setSelection([]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submitWord]);

  const wordValid = dict && currentWord.length >= 3 && dict.has(currentWord.toLowerCase()) && !foundWordsSet.has(currentWord.toLowerCase());
  const lowTime = phase === "playing" && timeLeft <= 30;

  // Admin-only gate (Protected already guarantees `user` is loaded)
  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  return (
    <div className="space-y-6" data-testid="word-game-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/app/games" className="inline-flex items-center gap-1 text-sm text-amber-700 hover:text-amber-900 mb-1" data-testid="back-to-games">
            <ArrowLeft className="w-4 h-4" /> Games
          </Link>
          <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2">
            <span className="text-yellow-500">🐝</span> Hive Words
          </h1>
          <p className="text-stone-500 mt-1">Click connected honeycomb cells to spell words before the timer runs out.</p>
        </div>
        <button onClick={() => setShowHelp((v) => !v)} className="inline-flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-900" data-testid="word-game-help-toggle">
          <HelpCircle className="w-4 h-4" /> How to play
        </button>
      </div>

      {showHelp && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5 text-sm text-stone-700 space-y-2" data-testid="word-game-help">
          <p><strong>Goal:</strong> spell as many words as you can before time runs out.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Pick a difficulty to set the timer, then click a letter and keep clicking <strong>connected cells</strong> to build a word.</li>
            <li>A letter in use is highlighted and cannot be reused in the same word. Click the last letter again (or press Backspace) to undo.</li>
            <li>Press <strong>Enter</strong> or tap <strong>Submit</strong>. Valid words (3+ letters) join your list — no duplicates. Then the cells reset for the next word.</li>
            <li><strong>Scramble</strong> shuffles the letters. When the timer hits zero your score is saved to the leaderboard.</li>
          </ul>
          <p className="pt-1"><strong>Scoring:</strong> 1 point per letter · <span className="text-amber-700 font-medium">+10 bonus</span> per letter for 6–9 letter words · <span className="text-amber-700 font-medium">+20 bonus</span> per letter for 10+ letter words.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ---------------- Main column ---------------- */}
        <div className="lg:col-span-2 space-y-5">
          {phase === "setup" ? (
            <SetupScreen onStart={startGame} best={myBest} />
          ) : (
            <>
              {/* Stat bar with timer */}
              <div className="flex flex-wrap items-center gap-3">
                <div className={`rounded-2xl px-5 py-3 shadow-sm transition-colors ${lowTime ? "bg-gradient-to-br from-rose-500 to-red-600 text-white animate-pulse" : "bg-gradient-to-br from-amber-400 to-yellow-500 text-amber-950"}`} data-testid="game-timer">
                  <div className="text-xs font-medium uppercase tracking-wide opacity-80 flex items-center gap-1"><Timer className="w-3 h-3" /> Time</div>
                  <div className="text-3xl font-bold tabular-nums leading-none">{fmtTime(timeLeft)}</div>
                </div>
                <div className="rounded-2xl bg-white border border-stone-200 px-5 py-3" data-testid="game-score">
                  <div className="text-xs font-medium uppercase tracking-wide text-stone-400">Score</div>
                  <div className="text-3xl font-bold tabular-nums leading-none text-stone-800">{gameScore}</div>
                </div>
                <div className="rounded-2xl bg-white border border-stone-200 px-5 py-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-stone-400">Words</div>
                  <div className="text-3xl font-bold tabular-nums leading-none text-stone-800">{found.length}</div>
                </div>
                <div className="rounded-2xl bg-white border border-stone-200 px-5 py-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-stone-400 flex items-center gap-1"><Trophy className="w-3 h-3" /> Best</div>
                  <div className="text-3xl font-bold tabular-nums leading-none text-stone-800" data-testid="my-best-score">{myBest?.best_score ?? 0}</div>
                </div>
              </div>

              {/* Current word */}
              <div className="min-h-[52px] flex items-center gap-3 flex-wrap">
                {selection.length > 0 ? (
                  <div className={`text-2xl font-display font-semibold tracking-[0.15em] px-4 py-2 rounded-xl border ${wordValid ? "bg-green-50 border-green-300 text-green-800" : "bg-stone-50 border-stone-200 text-stone-700"}`} data-testid="current-word">
                    {currentWord.toUpperCase()}
                    {wordValid && <span className="ml-2 text-base text-green-600">+{scoreWord(currentWord.toLowerCase())}</span>}
                  </div>
                ) : (
                  <div className="text-stone-400 italic">{phase === "over" ? "Time's up!" : "Pick a cell to start a word…"}</div>
                )}
              </div>

              {/* -------- Honeycomb board -------- */}
              {dictError && <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 p-4 text-sm">Couldn&apos;t load the word dictionary. Please refresh the page to try again.</div>}
              <div className="relative overflow-x-auto pb-2">
                {!dict && !dictError && (
                  <div className="absolute inset-0 z-20 grid place-items-center bg-white/60 backdrop-blur-sm rounded-xl">
                    <div className="flex items-center gap-2 text-stone-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading dictionary…</div>
                  </div>
                )}
                <div className="relative select-none" style={{ width: BOARD_W, height: BOARD_H }} data-testid="hex-board">
                  {/* Cohesive honeycomb grid drawn as an SVG background image */}
                  <svg width={BOARD_W} height={BOARD_H} viewBox={`0 0 ${BOARD_W} ${BOARD_H}`} className="absolute inset-0" style={{ pointerEvents: "none" }} aria-hidden="true">
                    <defs>
                      <linearGradient id="combFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fef9c3" />
                        <stop offset="100%" stopColor="#fde68a" />
                      </linearGradient>
                      <filter id="combShadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#a16207" floodOpacity="0.18" />
                      </filter>
                    </defs>
                    <g filter="url(#combShadow)">
                      {TILES.map((t) => (
                        <polygon key={t.id} points={hexPoints(t.cx, t.cy)} fill="url(#combFill)" stroke="#f59e0b" strokeWidth="2" strokeOpacity="0.55" strokeLinejoin="round" />
                      ))}
                    </g>
                  </svg>

                  {/* Invisible click grid + centred letters (only the letter highlights) */}
                  {TILES.map((t) => {
                    const idx = selection.indexOf(t.id);
                    const isSelected = idx >= 0;
                    const isLast = isSelected && idx === selection.length - 1;
                    return (
                      <button
                        key={t.id}
                        onClick={() => handleTileClick(t.id)}
                        disabled={phase !== "playing"}
                        data-testid={`hex-${t.id}`}
                        data-selected={isSelected}
                        className="absolute grid place-items-center focus:outline-none"
                        style={{
                          left: t.cx, top: t.cy, transform: "translate(-50%, -50%)",
                          width: SIZE * 1.55, height: SIZE * 1.55,
                          background: "transparent", border: "none",
                          cursor: phase === "playing" ? "pointer" : "default",
                        }}
                      >
                        <span
                          className="grid place-items-center font-bold text-lg uppercase"
                          style={{
                            width: SIZE * 1.12, height: SIZE * 1.12, borderRadius: "9999px",
                            background: isLast
                              ? "radial-gradient(circle at 35% 30%, #4ade80, #16a34a)"
                              : isSelected
                              ? "radial-gradient(circle at 35% 30%, #fbbf24, #d97706)"
                              : "transparent",
                            color: isSelected ? "#fff" : "#78350f",
                            boxShadow: isSelected ? "0 3px 12px rgba(180,83,9,0.45)" : "none",
                            transform: isSelected ? "scale(1.12)" : "none",
                            transition: "transform .12s ease, background .12s ease",
                            pointerEvents: "none",
                          }}
                        >
                          {letters[t.id]}
                        </span>
                      </button>
                    );
                  })}

                  {/* Game-over overlay */}
                  {phase === "over" && result && (
                    <div className="absolute inset-0 z-10 grid place-items-center rounded-xl bg-white/85 backdrop-blur-sm" data-testid="game-over">
                      <div className="text-center px-6 py-5 rounded-2xl bg-white border border-amber-200 shadow-lg max-w-xs">
                        <div className="text-4xl mb-1">{result.newRecord ? "🏆" : "🐝"}</div>
                        <div className="font-display text-2xl font-semibold text-stone-800">Time&apos;s up!</div>
                        <div className="mt-2 text-4xl font-bold text-amber-600 tabular-nums" data-testid="final-score">{result.score}</div>
                        <div className="text-sm text-stone-500">{result.words} word{result.words === 1 ? "" : "s"} found{result.bestWord ? ` · best: ${result.bestWord.toUpperCase()}` : ""}</div>
                        {result.newRecord && <div className="mt-1 text-sm font-medium text-green-600">New personal best!</div>}
                        <Button onClick={() => setPhase("setup")} className="mt-4 rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="play-again-button">
                          <Play className="w-4 h-4 mr-1.5" /> Play again
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Controls (only while playing) */}
              {phase === "playing" && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button onClick={submitWord} disabled={selection.length < 3} className="rounded-full bg-green-600 hover:bg-green-700 text-white" data-testid="submit-word-button">
                    <Check className="w-4 h-4 mr-1.5" /> Submit word
                  </Button>
                  <Button onClick={() => setSelection([])} variant="outline" disabled={selection.length === 0} className="rounded-full" data-testid="clear-selection-button">
                    <Eraser className="w-4 h-4 mr-1.5" /> Clear
                  </Button>
                  <Button onClick={scramble} variant="outline" className="rounded-full" data-testid="scramble-button">
                    <Shuffle className="w-4 h-4 mr-1.5" /> Scramble
                  </Button>
                  <Button onClick={endGame} variant="outline" className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="end-game-button">
                    <Flag className="w-4 h-4 mr-1.5" /> End game
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ---------------- Side panel ---------------- */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-amber-500" /> Words found ({found.length})</h2>
            {found.length === 0 ? (
              <p className="text-sm text-stone-400">No words yet — start a game and click cells!</p>
            ) : (
              <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="found-words-list">
                {found.map((f) => (
                  <li key={f.word} className="flex items-center justify-between text-sm border-b border-stone-100 pb-1.5">
                    <span className="font-medium uppercase tracking-wide text-stone-800">{f.word}</span>
                    <span className="text-amber-600 font-semibold tabular-nums">+{f.score}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-amber-500" /> Leaderboard</h2>
            {leaderboard.length === 0 ? (
              <p className="text-sm text-stone-400">Be the first to set a high score!</p>
            ) : (
              <ol className="space-y-2" data-testid="leaderboard-list">
                {leaderboard.map((e, i) => (
                  <li key={e.user_id} className="flex items-center gap-3 text-sm">
                    <span className={`w-6 h-6 shrink-0 grid place-items-center rounded-full text-xs font-bold ${i === 0 ? "bg-amber-400 text-amber-950" : i === 1 ? "bg-stone-300 text-stone-700" : i === 2 ? "bg-orange-300 text-orange-900" : "bg-stone-100 text-stone-500"}`}>{i + 1}</span>
                    <span className="flex-1 truncate text-stone-700">{e.user_name}</span>
                    <span className="font-semibold tabular-nums text-stone-800">{e.best_score}</span>
                  </li>
                ))}
              </ol>
            )}
            {myBest?.best_word && (
              <div className="mt-4 pt-3 border-t border-stone-100 text-xs text-stone-500 flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5 text-amber-500" />
                Your best word: <strong className="text-stone-700 uppercase">{myBest.best_word}</strong> (+{myBest.best_word_score})
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Difficulty setup screen
 * ------------------------------------------------------------------ */
function SetupScreen({ onStart, best }) {
  const [selected, setSelected] = useState("medium");
  const chosen = DIFFICULTIES.find((d) => d.key === selected);
  return (
    <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 p-6 md:p-8" data-testid="setup-screen">
      <h2 className="font-display text-xl font-semibold text-stone-800 flex items-center gap-2">
        <Zap className="w-5 h-5 text-amber-500" /> Choose your difficulty
      </h2>
      <p className="text-sm text-stone-500 mt-1">The clock starts as soon as you begin. Find as many words as you can!</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
        {DIFFICULTIES.map((d) => {
          const active = selected === d.key;
          return (
            <button
              key={d.key}
              onClick={() => setSelected(d.key)}
              data-testid={`difficulty-${d.key}`}
              className={`text-left rounded-xl border p-4 transition-all ${active ? "border-amber-500 bg-white shadow-md ring-2 ring-amber-300" : "border-stone-200 bg-white/70 hover:border-amber-300"}`}
            >
              <div className="text-2xl">{d.icon}</div>
              <div className="mt-1 font-semibold text-stone-800">{d.label}</div>
              <div className="text-2xl font-bold tabular-nums text-amber-600 mt-0.5">{fmtTime(d.seconds)}</div>
              <div className="text-xs text-stone-500 mt-0.5">{d.blurb}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button onClick={() => onStart(chosen)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-6" data-testid="start-game-button">
          <Play className="w-4 h-4 mr-1.5" /> Start game ({fmtTime(chosen.seconds)})
        </Button>
        {best?.best_score > 0 && (
          <span className="text-sm text-stone-500 inline-flex items-center gap-1.5">
            <Trophy className="w-4 h-4 text-amber-500" /> Your best: <strong className="text-stone-700">{best.best_score}</strong> pts
          </span>
        )}
      </div>
    </div>
  );
}
